import { Spacing } from '@persona-id/design-tokens';
import { EditIcon } from '@persona-id/design-tokens/icons';
import { groupBy } from 'lodash/fp';
import { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import {
  filterUsableObjects,
  REPORT_OBJECT_KEYS,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import {
  WorkflowActionMeta,
  WorkflowError,
  WorkflowFieldObjectKey,
} from 'dashboard/models/workflow';
import { WorkflowRedactableFieldConfigField } from 'dashboard/models/workflow-schema';
import {
  ACTIONS_THAT_CREATE_REPORTS,
  StaticWorkflowAction,
  WorkflowActionRedactObjectV1,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { useGetRedactableFieldConfigsQuery } from 'dashboard/redux/api/workflow-schema';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Radio from 'design-system/components/Radio/Radio';
import RadioGroup from 'design-system/components/RadioGroup/RadioGroup';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

const SET_OF_ACTIONS_THAT_CREATE_REPORTS = new Set(ACTIONS_THAT_CREATE_REPORTS);

enum RedactOptions {
  ALL = 'redact-all',
  SELECTED = 'redact-selected',
}

enum RedactableFieldKeys {
  TemplateId = 'template-id',
  TemplateName = 'template-name',
}

const MIN_RETENTION_PERIOD_DAYS = 0;
const MAX_RETENTION_PERIOD_DAYS = 365;

const SelectFieldsToggleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const FieldLabel = styled.span`
  margin-left: ${Spacing[2]};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
`;

const FieldRow = styled.div`
  margin-left: ${Spacing[4]};
`;

const CheckboxContainer = styled.div`
  margin-right: ${Spacing[2]};
  display: flex;
  align-items: center;
`;

const AvailableFieldsContainer = styled.div`
  margin-bottom: ${Spacing[4]};
  margin-right: ${Spacing[4]};
`;

const TemplateNameContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

interface FieldSelectPanelProps {
  availableFields: WorkflowRedactableFieldConfigField[];
  onOutsideClick: () => void;
  selectedFields: { [RedactableFieldKeys.TemplateId]: string; path: string }[];
  templateIds: string[];
  templateIdsToTemplateNames: Record<string, string>;
  title: string;
  updateSelectedFields: (
    value: { [RedactableFieldKeys.TemplateId]: string; path: string }[],
  ) => void;
}

const getTemplateIds = (availableFields: WorkflowRedactableFieldConfigField[]) =>
  Array.from(new Set(availableFields.map((af) => af[RedactableFieldKeys.TemplateId])));

const FieldSelectPanel: FC<FieldSelectPanelProps> = ({
  availableFields,
  onOutsideClick,
  selectedFields,
  templateIds,
  templateIdsToTemplateNames,
  title,
  updateSelectedFields,
}) => {
  const availableFieldsForTemplate = useMemo(
    () => groupBy(RedactableFieldKeys.TemplateId, availableFields),
    [availableFields],
  );

  const selectedFieldsForTemplate = useMemo(
    () => groupBy(RedactableFieldKeys.TemplateId, selectedFields),
    [selectedFields],
  );

  const handleSelectField = (value: { [RedactableFieldKeys.TemplateId]: string; path: string }) => {
    if (
      selectedFields.some(
        (element) =>
          element[RedactableFieldKeys.TemplateId] === value[RedactableFieldKeys.TemplateId] &&
          element.path === value.path,
      )
    ) {
      updateSelectedFields(
        selectedFields.filter(
          (sf) =>
            !(
              sf[RedactableFieldKeys.TemplateId] === value[RedactableFieldKeys.TemplateId] &&
              sf.path === value.path
            ),
        ),
      );
    } else {
      updateSelectedFields([value, ...selectedFields]);
    }
  };

  const toggleCheckAllInTemplate = useCallback(
    (
      templateId: string,
      checked: boolean,
      availableFieldsForTemplate: WorkflowRedactableFieldConfigField[],
    ) => {
      updateSelectedFields([
        ...selectedFields.filter(
          (selected) => selected[RedactableFieldKeys.TemplateId] !== templateId,
        ),
        ...(!checked
          ? availableFieldsForTemplate.map((field) => ({
              [RedactableFieldKeys.TemplateId]: field[RedactableFieldKeys.TemplateId],
              path: field.path,
            }))
          : []),
      ]);
    },
    [selectedFields, updateSelectedFields],
  );

  return (
    <MaximizablePanel title={title} onOutsideClickAndEscPress={onOutsideClick}>
      {templateIds.map((templateId) => {
        const checked =
          selectedFieldsForTemplate[templateId]?.length ===
          availableFieldsForTemplate[templateId].length;
        return (
          <AvailableFieldsContainer key={templateId}>
            <TemplateNameContainer
              aria-label={`Template: ${templateIdsToTemplateNames[templateId]}`}
            >
              {templateIdsToTemplateNames[templateId]}
              <Checkbox
                checked={checked}
                indeterminate={
                  selectedFieldsForTemplate[templateId]?.length > 0 &&
                  selectedFieldsForTemplate[templateId]?.length <
                    availableFieldsForTemplate[templateId].length
                }
                id={templateId}
                onChange={() =>
                  toggleCheckAllInTemplate(
                    templateId,
                    checked,
                    availableFieldsForTemplate[templateId],
                  )
                }
                label={`${checked ? 'Unselect' : 'Select'} all in template`}
              />
            </TemplateNameContainer>
            {availableFieldsForTemplate[templateId].map((af) => (
              <CheckboxContainer key={[af[RedactableFieldKeys.TemplateId], af.path].join(':')}>
                <Checkbox
                  checked={selectedFields.some(
                    (element) =>
                      element[RedactableFieldKeys.TemplateId] ===
                        af[RedactableFieldKeys.TemplateId] && element.path === af.path,
                  )}
                  label={<FieldLabel>{af.label}</FieldLabel>}
                  name={`field-select-checkbox__-${
                    af[RedactableFieldKeys.TemplateId] + ':' + af.path
                  }`}
                  onChange={() =>
                    handleSelectField({
                      [RedactableFieldKeys.TemplateId]: af[RedactableFieldKeys.TemplateId],
                      path: af.path,
                    })
                  }
                />
              </CheckboxContainer>
            ))}
          </AvailableFieldsContainer>
        );
      })}
    </MaximizablePanel>
  );
};

const stepTokenToTemplate = (
  usableObjects: UsableObjectConfig[],
  actionMeta: WorkflowActionMeta,
  enableRedactionOldReportActions: boolean,
): Record<string, string> => {
  const reportTemplates = actionMeta['report-templates'];
  return usableObjects.reduce<Record<string, string>>((accum, { filters, step }) => {
    if (step && filters?.['template-filters']?.length) {
      const token = step.type === WorkflowStepType.Input ? 'trigger' : step.token;
      accum[token] = filters['template-filters'][0].id;
    } else if (
      step?.type === WorkflowStepType.Action &&
      SET_OF_ACTIONS_THAT_CREATE_REPORTS.has((step.action as StaticWorkflowAction).key) &&
      enableRedactionOldReportActions
    ) {
      const reportTemplateToken = reportTemplates.find(
        ({ type }) =>
          type === step.action.key.replace('run_report_', 'report-template/').replace(/_/g, '-'),
      )?.token;
      if (reportTemplateToken) {
        accum[step.token] = reportTemplateToken;
      }
    }

    return accum;
  }, {});
};

interface ParamsRedactObjectV1Props extends ParamsBaseProps {
  actionMeta: WorkflowActionMeta;
  editable: boolean;
  errors: WorkflowError[];
  focusStep: (token: string) => void;
  onMenuClose: () => void;
  params: WorkflowActionRedactObjectV1['params'];
  trigger?: Trigger;
  updateActionParams: (newParams: WorkflowActionRedactObjectV1['params']) => void;
  usableObjects: UsableObjectConfig[];
}

const ParamsRedactObjectV1: FC<ParamsRedactObjectV1Props> = ({
  actionMeta,
  editable,
  errors,
  focusStep,
  onMenuClose,
  params,
  step,
  trigger,
  updateActionParams,
  updateStep,
  usableObjects,
}) => {
  const enableRestore = useFeatureFlag('workflows:enable-redaction-restore');
  const enableRedactionOldReportActions = useFeatureFlag(
    'dashboard:enable-workflows-redactions-old-report-actions',
  );
  const { data: redactableFieldConfigs = [], isLoading } = useGetRedactableFieldConfigsQuery();

  const templateIdsToTemplateNames = useMemo(
    () =>
      redactableFieldConfigs.reduce<Record<string, string>>(
        (acc, rfc) => ({
          ...acc,
          ...rfc.fields.reduce(
            (rfcFields, field) => ({
              ...rfcFields,
              [field[RedactableFieldKeys.TemplateId]]: field['template-name'],
            }),
            {},
          ),
        }),
        {},
      ),
    [redactableFieldConfigs],
  );

  const filteredUsableObjects = useMemo(
    () =>
      filterUsableObjects({
        usableObjects,
        options: {
          permittedObjects: [
            WorkflowFieldObjectKey.Inquiry,
            WorkflowFieldObjectKey.Account,
            WorkflowFieldObjectKey.Case,
            WorkflowFieldObjectKey.Transaction,
            ...REPORT_OBJECT_KEYS,
          ],
        },
      }),
    [usableObjects],
  );

  const objectsToTemplates = useMemo(
    () => stepTokenToTemplate(filteredUsableObjects, actionMeta, enableRedactionOldReportActions),
    [filteredUsableObjects, actionMeta, enableRedactionOldReportActions],
  );

  const [showEditPanel, { toggle: toggleEditPanel }] = useToggle(false);

  const selectObject = (object: WorkflowActionRedactObjectV1['params']['object']) => {
    updateActionParams({
      ...params,
      fields: [],
      object,
      'redact-all': true,
    });
  };

  const updateFields = (fields: { [RedactableFieldKeys.TemplateId]: string; path: string }[]) => {
    updateActionParams({
      ...params,
      fields: fields,
    });
  };

  const selectRedactOption = (value: string) => {
    const redactAll = value === RedactOptions.ALL;
    updateActionParams({
      ...params,
      [RedactOptions.ALL]: redactAll,
      fields: redactAll ? [] : params['fields'],
    });
  };

  const updatePurgeAfterDays = (value: number) => {
    updateActionParams({
      ...params,
      'retention-period-days':
        !isNaN(value) && value >= MIN_RETENTION_PERIOD_DAYS ? value : MIN_RETENTION_PERIOD_DAYS,
    });
  };

  const availableFields = useMemo(() => {
    const templatesToFields = redactableFieldConfigs.reduce<
      Record<string, WorkflowRedactableFieldConfigField[]>
    >(
      (acc, { fields, token }) => ({
        ...acc,
        ...(fields.length > 0 ? { [token]: fields } : {}),
      }),
      {},
    );

    return (
      templatesToFields[objectsToTemplates[params['object']]] ||
      templatesToFields[
        filteredUsableObjects.find((o) => o.value === params['object'])?.workflowObjectKey ?? ''
      ] ||
      []
    );
  }, [redactableFieldConfigs, params, filteredUsableObjects, objectsToTemplates]);

  const templateIds = useMemo(() => {
    const ids = getTemplateIds(availableFields).sort((a, b) =>
      templateIdsToTemplateNames[a].localeCompare(templateIdsToTemplateNames[b]),
    );
    if (ids.includes('core_attributes')) {
      return [...ids.filter((id) => id !== 'core_attributes')];
    }
    return ids;
  }, [availableFields, templateIdsToTemplateNames]);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionRedactObjectV1['params']>(errors);

  /*
   * We default retention period to 0 for all trigger types except recurring.
   * a) this preserves behaviour for workflows that predate redaction restore
   * b) the 30 day default for recurring triggersis to add some initial safety for
   *    users implementing/debugging their trigger queries.
   */
  const DEFAULT_RETENTION_PERIOD_DAYS = trigger?.type === ApiModel.TriggerRecurring ? 30 : 0;

  const HELP_TEXT = params['retention-period-days']
    ? `Data redacted by this action will be restorable for ${params['retention-period-days']} days`
    : 'Data will be immediately redacted';

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Object' htmlFor='params-redact-object__object-input' />
      <ReactSelect
        value={params.object}
        options={filteredUsableObjects}
        onChange={selectObject}
        id='params-redact-object__object'
        disabled={!editable}
        onFocusOption={focusStep}
        onMenuClose={onMenuClose}
        hasError={hasErrorFor('object')}
      />
      {renderErrorMessageFor('object')}
      {params['object'] && (
        <>
          <Spacer height={2} />
          <ParamLabel
            labelText='Fields to redact'
            htmlFor='params-redact-object__fields-to-redact-input'
          />
          <RadioGroup id='params-redact-object__options'>
            <Radio
              label='Personally Identifiable Information'
              disabled={!editable}
              checked={params[RedactOptions.ALL] || availableFields.length < 1}
              value={RedactOptions.ALL}
              onChange={() => selectRedactOption(RedactOptions.ALL)}
            />
            {isLoading ? (
              <LoadingIndicator />
            ) : (
              availableFields.length > 0 && (
                <SelectFieldsToggleRow>
                  <Radio
                    label='Selected Fields'
                    disabled={!editable}
                    value={RedactOptions.SELECTED}
                    checked={!params[RedactOptions.ALL]}
                    onChange={() => selectRedactOption(RedactOptions.SELECTED)}
                  />
                  {!params[RedactOptions.ALL] && (
                    <HStack>
                      <Button
                        variant='basic'
                        onClick={toggleEditPanel}
                        id='params-redact-object__selected-fields'
                        suffixIcon={<EditIcon />}
                      >
                        Select Fields
                      </Button>
                    </HStack>
                  )}
                </SelectFieldsToggleRow>
              )
            )}
          </RadioGroup>
          {renderErrorMessageFor('fields')}
        </>
      )}
      <Spacer height={2} />
      {showEditPanel && !params[RedactOptions.ALL] && (
        <FieldSelectPanel
          templateIds={templateIds}
          templateIdsToTemplateNames={templateIdsToTemplateNames}
          availableFields={availableFields || []}
          title='Redact selected fields'
          selectedFields={params['fields']}
          onOutsideClick={toggleEditPanel}
          updateSelectedFields={updateFields}
        />
      )}
      {params['fields']?.length > 0 && (
        <>
          {templateIds.map((templateId) => {
            const fieldsByTemplate = availableFields.filter(
              (af) =>
                af[RedactableFieldKeys.TemplateId] === templateId &&
                params['fields'].some(
                  (element) =>
                    element[RedactableFieldKeys.TemplateId] ===
                      af[RedactableFieldKeys.TemplateId] && element.path === af.path,
                ),
            );

            if (fieldsByTemplate?.length > 0) {
              return (
                <AvailableFieldsContainer key={templateId}>
                  <div>{templateIdsToTemplateNames[templateId]}</div>
                  {fieldsByTemplate.map(({ label }) => {
                    return (
                      <FieldRow key={label}>
                        <FieldLabel>{label}</FieldLabel>
                      </FieldRow>
                    );
                  })}
                </AvailableFieldsContainer>
              );
            }
          })}
        </>
      )}
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        {!params[RedactOptions.ALL] && enableRestore && (
          <>
            <Spacer height={2} />
            <ParamLabel
              labelText='Days until permanently deleted'
              htmlFor='params-redact-object__retention-period-days'
              helpText={HELP_TEXT}
            />
            <Input
              type='number'
              min={MIN_RETENTION_PERIOD_DAYS}
              max={MAX_RETENTION_PERIOD_DAYS}
              value={params['retention-period-days']}
              onChange={(e) => updatePurgeAfterDays(Number(e.target.value))}
              disabled={!editable}
              placeholder={String(DEFAULT_RETENTION_PERIOD_DAYS)}
              id='params-redact-object__retention-period-days'
              hasError={hasErrorFor('retention-period-days')}
            />
            {renderErrorMessageFor('retention-period-days')}
          </>
        )}
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsRedactObjectV1;
